import { z } from "zod";
/**
 * Uniform error envelope for every failing endpoint (ADR-0002).
 * `details` carries Zod field errors; a 409 body may embed current state via
 * the `conflict` field (optional, populated by the api layer).
 */
export const errorEnvelopeSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.array(z.object({
            path: z.array(z.string()),
            message: z.string(),
        })).optional(),
        /** Current container state embedded in a 409 conflict body (ADR-0002). */
        current: z.unknown().optional(),
    }),
});
/**
 * Single source of truth for error codes. The array feeds both the union type
 * and the status mapping, so adding a code touches exactly one place.
 */
export const ERROR_CODES = [
    "validation",
    "not_found",
    "conflict",
    "unauthorized",
    "forbidden",
    "rate_limited",
    "internal",
    "too_large",
];
export function isErrorCode(value) {
    return typeof value === "string" && ERROR_CODES.includes(value);
}
/** Maps an HTTP status to the canonical error code; unknown 5xx => "internal". */
export function errorCodeForStatus(status) {
    if (status === 401)
        return "unauthorized";
    if (status === 403)
        return "forbidden";
    if (status === 404)
        return "not_found";
    if (status === 409)
        return "conflict";
    if (status === 413)
        return "too_large";
    if (status === 429)
        return "rate_limited";
    if (status >= 400 && status < 500)
        return "validation";
    return "internal";
}
export function toErrorEnvelope(code, message, details) {
    return { error: { code, message, details } };
}
/** Flattens a ZodError into the envelope's `details` field shape. */
export function zodErrorDetails(error) {
    return error.issues.map((issue) => ({
        path: issue.path.map(String),
        message: issue.message,
    }));
}
/** A validated request that failed → 400 with Zod field details. */
export function validationEnvelope(error, message = "Invalid request") {
    return { error: { code: "validation", message, details: zodErrorDetails(error) } };
}
/** A stale-write rejection → 409 embedding the current container state. */
export function conflictEnvelope(current, message = "Version conflict — the container changed since it was read") {
    return { error: { code: "conflict", message, current } };
}
//# sourceMappingURL=errors.js.map